import { getStoredCookieConsent } from "./cookieConsent";

const TRACKING_ENABLED =
  process.env.REACT_APP_ENABLE_ANALYTICS !== "false";

const GA_MEASUREMENT_ID = TRACKING_ENABLED
  ? String(process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim()
  : "";

const META_PIXEL_ID = TRACKING_ENABLED
  ? String(process.env.REACT_APP_META_PIXEL_ID || "").trim()
  : "";

const TRACKING_STATE_KEY = "__weddingStationeryTracking";
const PURCHASE_STORAGE_PREFIX = "tracked-purchase:";

const getTrackingState = () => {
  if (!window[TRACKING_STATE_KEY]) {
    window[TRACKING_STATE_KEY] = {
      googleInitialized: false,
      googleConsentConfigured: false,
      metaInitialized: false,
    };
  }

  return window[TRACKING_STATE_KEY];
};

const getConsent = () => {
  const consent = getStoredCookieConsent();

  return {
    analytics: consent?.analytics === true,
    marketing: consent?.marketing === true,
  };
};

const appendScript = ({ id, src }) => {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const ensureGoogleTag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments);
    };
};

const updateGoogleConsent = (consent) => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  const trackingState = getTrackingState();
  const command = trackingState.googleConsentConfigured
    ? "update"
    : "default";

  window.gtag("consent", command, {
    analytics_storage: consent.analytics ? "granted" : "denied",
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
  });

  trackingState.googleConsentConfigured = true;
};

const initializeGoogleAnalytics = (consent) => {
  if (!GA_MEASUREMENT_ID) return;

  const trackingState = getTrackingState();

  if (!consent.analytics) {
    if (trackingState.googleInitialized) {
      updateGoogleConsent(consent);
    }
    return;
  }

  ensureGoogleTag();
  updateGoogleConsent(consent);

  if (trackingState.googleInitialized) return;

  appendScript({
    id: "google-analytics-gtag",
    src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GA_MEASUREMENT_ID
    )}`,
  });

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  trackingState.googleInitialized = true;
};

const ensureMetaPixel = () => {
  if (window.fbq) return;

  const fbq = function fbq() {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, arguments);
      return;
    }

    fbq.queue.push(arguments);
  };

  window._fbq = fbq;
  window.fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
};

const initializeMetaPixel = (consent) => {
  if (!META_PIXEL_ID) return;

  const trackingState = getTrackingState();

  if (!consent.marketing) {
    if (trackingState.metaInitialized && window.fbq) {
      window.fbq("consent", "revoke");
    }
    return;
  }

  ensureMetaPixel();

  if (!trackingState.metaInitialized) {
    appendScript({
      id: "meta-pixel-script",
      src: "https://connect.facebook.net/en_US/fbevents.js",
    });

    window.fbq("init", META_PIXEL_ID);
    trackingState.metaInitialized = true;
  }

  window.fbq("consent", "grant");
};

export const initializeTracking = (consent = getConsent()) => {
  initializeGoogleAnalytics(consent);
  initializeMetaPixel(consent);
};

const trackGoogleEvent = (eventName, parameters = {}) => {
  const consent = getConsent();

  if (!GA_MEASUREMENT_ID || !consent.analytics) return false;

  initializeGoogleAnalytics(consent);
  window.gtag("event", eventName, parameters);
  return true;
};

const trackMetaEvent = (eventName, parameters = {}) => {
  const consent = getConsent();

  if (!META_PIXEL_ID || !consent.marketing) return false;

  initializeMetaPixel(consent);
  window.fbq("track", eventName, parameters);
  return true;
};

const getProductPrice = (product) =>
  Number(
    product?.price ??
      product?.final_price ??
      product?.unit_price ??
      0
  );

const getProductId = (product) =>
  String(
    product?.product_id ??
      product?.id ??
      product?.sku ??
      ""
  );

const getProductName = (product) =>
  product?.product_name || product?.name || "Product";

const toGoogleItem = (product, quantity = 1) => ({
  item_id: getProductId(product),
  item_name: getProductName(product),
  item_category:
    product?.category?.name || product?.category || undefined,
  item_category2:
    product?.sub_category?.name || product?.sub_category || undefined,
  price: getProductPrice(product),
  quantity: Number(quantity) || 1,
});

const toOrderProduct = (item) => {
  const snapshot = item?.product_snapshot || {};

  return {
    ...snapshot,
    product_id: item?.product_id || snapshot.product_id,
    product_name:
      snapshot.name || snapshot.product_name || "Product",
    price: Number(item?.unit_price || snapshot.price || 0),
  };
};

export const trackPageView = ({ path, title }) => {
  trackGoogleEvent("page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: title || document.title,
  });

  trackMetaEvent("PageView");
};

export const trackSearch = (searchTerm) => {
  const term = String(searchTerm || "").trim();
  if (!term) return;

  trackGoogleEvent("view_search_results", {
    search_term: term,
  });

  trackMetaEvent("Search", {
    search_string: term,
  });
};

export const trackViewContent = (product) => {
  const value = getProductPrice(product);

  trackGoogleEvent("view_item", {
    currency: "INR",
    value,
    items: [toGoogleItem(product)],
  });

  trackMetaEvent("ViewContent", {
    content_ids: [getProductId(product)],
    content_name: getProductName(product),
    content_category: product?.category?.name,
    content_type: "product",
    currency: "INR",
    value,
  });
};

export const trackAddToWishlist = (product) => {
  const value = getProductPrice(product);

  trackGoogleEvent("add_to_wishlist", {
    currency: "INR",
    value,
    items: [toGoogleItem(product)],
  });

  trackMetaEvent("AddToWishlist", {
    content_ids: [getProductId(product)],
    content_name: getProductName(product),
    content_type: "product",
    currency: "INR",
    value,
  });
};

export const trackAddToCart = (product, value) => {
  const itemValue = Number(value ?? getProductPrice(product));
  const trackedProduct = {
    ...product,
    price: itemValue,
  };

  trackGoogleEvent("add_to_cart", {
    currency: "INR",
    value: itemValue,
    items: [toGoogleItem(trackedProduct)],
  });

  trackMetaEvent("AddToCart", {
    content_ids: [getProductId(product)],
    content_name: getProductName(product),
    content_type: "product",
    currency: "INR",
    value: itemValue,
  });
};

export const trackBeginCheckout = ({ items = [], value = 0 }) => {
  const googleItems = items.map((item) => {
    const product = toOrderProduct(item);
    return toGoogleItem(product, item?.quantity);
  });
  const contentIds = googleItems
    .map((item) => item.item_id)
    .filter(Boolean);

  trackGoogleEvent("begin_checkout", {
    currency: "INR",
    value: Number(value) || 0,
    items: googleItems,
  });

  trackMetaEvent("InitiateCheckout", {
    content_ids: contentIds,
    content_type: "product",
    currency: "INR",
    num_items: googleItems.reduce(
      (total, item) => total + Number(item.quantity || 1),
      0
    ),
    value: Number(value) || 0,
  });
};

export const trackPurchase = (order) => {
  const transactionId = String(
    order?.order_number || order?.order_id || ""
  );

  if (!transactionId) return;

  const storageKey = `${PURCHASE_STORAGE_PREFIX}${transactionId}`;

  try {
    if (window.sessionStorage.getItem(storageKey)) return;
  } catch {
    // Tracking can continue when session storage is unavailable.
  }

  const consent = getConsent();
  const canTrackGoogle = Boolean(
    GA_MEASUREMENT_ID && consent.analytics
  );
  const canTrackMeta = Boolean(META_PIXEL_ID && consent.marketing);

  if (!canTrackGoogle && !canTrackMeta) return;

  const items = Array.isArray(order?.items) ? order.items : [];
  const googleItems = items.map((item) =>
    toGoogleItem(toOrderProduct(item), item?.quantity)
  );
  const value = Number(order?.total_amount || 0);
  const currency = order?.currency || "INR";

  trackGoogleEvent("purchase", {
    transaction_id: transactionId,
    currency,
    value,
    shipping: Number(order?.shipping_fee || 0),
    items: googleItems,
  });

  trackMetaEvent("Purchase", {
    content_ids: googleItems
      .map((item) => item.item_id)
      .filter(Boolean),
    content_type: "product",
    currency,
    num_items: googleItems.reduce(
      (total, item) => total + Number(item.quantity || 1),
      0
    ),
    value,
  });

  try {
    window.sessionStorage.setItem(storageKey, "true");
  } catch {
    // The provider transaction ID still helps deduplicate where supported.
  }
};

export const trackingConfiguration = {
  hasGoogleAnalytics: Boolean(GA_MEASUREMENT_ID),
  hasMetaPixel: Boolean(META_PIXEL_ID),
};
